import React, { useEffect, useState } from 'react';
import AdminLayout from '../../components/layout/AdminLayout';
import Card from '../../components/common/Card';
import {
  DashboardClient,
  SearchRequestDTO_dashboardLeadCountSearchRequestDTO,
  UserClient,
} from '../../services/apiClient';
import { API_BASE_URL } from '../../config/config';
import axiosInstance from '../../services/axiosInstance';
import { getCurrentWeek } from '../../utils/getCurrentWeek';
import UserLeads from '../../components/dashboard/UserLeads';
import { fetchProfilePictures } from '../../utils/user/fetchProfilePictures';
import Loader from '../../components/common/Loader';

interface DashboardUserLeads {
  userId: string;
  profilePictureUrl: string;
  firstName: string;
  lastName: string;
  jobTitle: string;
  leadCount: number;
}

const Dashboard: React.FC = () => {
  const breadcrumbs = [
    { label: 'Admin', href: '#' },
    { label: 'Dashboard', href: '#' },
  ];

  const [userLeadCounts, setUserLeadCounts] = useState<DashboardUserLeads[]>(
    [],
  );
  const [isLoading, setIsLoading] = useState(false);

  const apiClient = new DashboardClient(API_BASE_URL, axiosInstance);
  const userClient = new UserClient(API_BASE_URL, axiosInstance);

  const fetchUserLeadsCount = async () => {
    const [startDate, endDate] = getCurrentWeek();

    const searchRequest: SearchRequestDTO_dashboardLeadCountSearchRequestDTO = {
      filters: {
        startDate,
        endDate,
      },
      page: 1,
      pageSize: 1,
      sortBy: 'string',
      sortDescending: true,
    };

    try {
      setIsLoading(true);
      const response = await apiClient.getUserLeads(searchRequest);
      const userLeadsRaw = response.data?.results ?? [];

      const initialUserLeads: DashboardUserLeads[] = userLeadsRaw.map(
        (user: any) => ({
          userId: user.userId,
          firstName: user.firstName,
          lastName: user.lastName,
          jobTitle: user.jobTitle || 'No title',
          leadCount: user.leadCount,
          profilePictureUrl: '',
        }),
      );

      setUserLeadCounts(initialUserLeads);

      const picUrls = await fetchProfilePictures(initialUserLeads);

      setUserLeadCounts((prev) =>
        prev.map((user, i) => ({
          ...user,
          profilePictureUrl: picUrls[i],
        })),
      );
    } catch (error) {
      console.error(`Error fetching user lead count:`, error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchUserLeadsCount();
  }, []);

  return (
    <AdminLayout
      activeMenuItem="/admin/dashboard"
      breadcrumbs={breadcrumbs}
      allowedRoles={['Administrator', 'User']}
    >
      <div className="flex justify-between w-full">
        <Card title="Personal Information">
          <div className="flex flex-wrap">
            {isLoading ? (
              <div className='m-auto'>
                <Loader />
              </div>
            ) : userLeadCounts.length === 0 ? (
              <div className='m-auto text-center text-slate-500'>No User Data</div>
            ) : (
              userLeadCounts.map((user) => (
                <UserLeads
                  profilePictureUrl={user.profilePictureUrl}
                  firstName={user.firstName}
                  lastName={user.lastName}
                  jobTitle={user.jobTitle}
                  leads={user.leadCount}
                />
              ))
            )}
          </div>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default Dashboard;
